import { useEffect } from 'react';

import {
  getPagePath,
  useSoyuzData,
  useSoyuzLocales,
  useSoyuzServer,
  useSoyuzRequest
} from '@spotahome/soyuz/client';
import {
  ThemeProvider,
  CookiesProvider,
  ToastContainer,
  GoogleOneTap,
  UserInfoProvider,
  HotspotsProvider,
  CitiesProvider,
  FiltersProvider,
  FacetSearchProvider,
  LoginSignupModalWrapper
} from '@spotahome/ui-library';
import ZohoChatbot from '@spotahome/ui-library/src/tenant/components/ZohoChatbot/ZohoChatbot';

import { configureMetricsActions } from '@spotahome/seo-landings-common/src/common/graphql/mutations/tenantActions';
import { loadModules } from '@spotahome/seo-landings-common/src/configurator';
import OptInModalWrapper from '@spotahome/seo-landings-common/src/components/OptInModalWrapper';
import SeoLocalesContainer from '@spotahome/seo-landings-common/src/components/SeoLocalesContainer';
import { SeoCitiesProvider } from '@spotahome/seo-landings-common/src/context/SeoCitiesContext';
import SeoPageWrapper from '@spotahome/seo-landings-common/src/components/SeoPageWrapper';
import NotFound from '@spotahome/seo-landings-common/src/components/NotFound';

import '@spotahome/seo-landings-common/src/scss/init.scss';

const buildRouting = (currentRoute, currentSlug, availableLocales) => {
  return availableLocales.reduce(
    (acc, locale) => ({
      ...acc,
      [locale]: getPagePath(currentRoute, {
        locale,
        params: {
          slug: currentSlug
        }
      })
    }),
    {}
  );
};

const Location = () => {
  const { currentRoute } = useSoyuzServer();
  const { routes } = useSoyuzLocales();
  const { params } = useSoyuzRequest();
  const availableLocales = Object.keys(routes);
  const { [Location.NAME]: requestedData } = useSoyuzData();
  const {
    userId,
    initialCookie,
    modules,
    currentCity,
    deviceType,
    error,
    initialFilters
  } = requestedData;
  const isUserLogged = !!userId;

  const routing = buildRouting(currentRoute, params.slug, availableLocales);

  useEffect(() => {
    configureMetricsActions();
  }, []);

  if (error === 'notFound') {
    return (
      <NotFound
        initialCookie={initialCookie}
        isUserLogged={isUserLogged}
        deviceType={deviceType}
      />
    );
  }

  return (
    <CookiesProvider initialCookie={initialCookie}>
      <ThemeProvider flags={{ newButtons: true }}>
        <UserInfoProvider>
          <HotspotsProvider>
            <CitiesProvider skipAllCities>
              <ToastContainer />
              <ZohoChatbot />
              <FiltersProvider
                initialFilters={initialFilters}
                cityId={currentCity.id}
                shouldUpdateUrl={false}
              >
                <FacetSearchProvider
                  cityId={currentCity.id}
                  initialFilters={initialFilters}
                >
                  <SeoLocalesContainer routing={routing}>
                    <SeoCitiesProvider currentCity={currentCity}>
                      <SeoPageWrapper
                        modules={modules}
                        loadModules={loadModules}
                        deviceType={deviceType}
                      />
                    </SeoCitiesProvider>
                    {!isUserLogged && <GoogleOneTap />}
                    <OptInModalWrapper />
                    <LoginSignupModalWrapper />
                  </SeoLocalesContainer>
                </FacetSearchProvider>
              </FiltersProvider>
            </CitiesProvider>
          </HotspotsProvider>
        </UserInfoProvider>
      </ThemeProvider>
    </CookiesProvider>
  );
};

Location.NAME = 'LocationPage';

export default Location;
